<script setup>
import {computed} from 'vue';
import {settingsBreadcrumbsForKey} from './breadcrumbs';

const props = defineProps({
    user: {
        type: Object,
        required: true,
    },
    tokens: {
        type: Array,
        default: () => [],
    },
    analyses: {
        type: Array,
        default: () => [],
    },
    showCreatorSettings: {
        type: Boolean,
        default: false,
    },
    hasCompany: {
        type: Boolean,
        default: false,
    },
    cronofyClientId: {
        type: String,
        default: '',
    },
    cronofyRedirectUri: {
        type: String,
        default: '',
    },
    cronofyEmbedToken: {
        type: String,
        default: null,
    },
    cronofyNonce: {
        type: String,
        default: '',
    },
    timezone: {
        type: String,
        default: '',
    },
    defaultCalendarSelected: {
        type: Boolean,
        default: false,
    },
});

const breadcrumbs = computed(() => settingsBreadcrumbsForKey('navigation:backOffice.sidebar.myCompany.settings.userprofile'));
const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

const formattedDate = (value) => {
    if (!value) {
        return '';
    }

    return value.slice(0, 10);
};
</script>

<template>
    <div class="w-full max-w-5xl mx-auto">
        <frosted-bar :breadcrumbs="breadcrumbs">
            <div class="flex items-center justify-between space-x-4">
                <h1>{{ _mft('navigation:backOffice.sidebar.myCompany.settings.userprofile') }}</h1>
            </div>
        </frosted-bar>

        <card-tabbed class="mt-8">
            <card-tabbed-tab
                title="user:editProfile.title"
                :localizetitle="true"
                value="profile"
                :active="true"
            >
                <manage-user-profile
                    :user="props.user"
                    class="pb-12"
                />
            </card-tabbed-tab>

            <card-tabbed-tab
                v-if="props.showCreatorSettings"
                title="user:calendarIntegration.title"
                :localizetitle="true"
                value="calendar-integration"
            >
                <page-expert-profile-availability
                    :cronofy-client-id="props.cronofyClientId"
                    :cronofy-redirect-uri="props.cronofyRedirectUri"
                    :cronofy-embed-token="props.cronofyEmbedToken"
                    :cronofy-nonce="props.cronofyNonce"
                    :timezone="props.timezone"
                    :default-calendar-selected="props.defaultCalendarSelected"
                />
            </card-tabbed-tab>

            <card-tabbed-tab
                v-if="props.showCreatorSettings"
                title="user:meetingSettings.title"
                :localizetitle="true"
                value="meeting-preferences"
            >
                <coaching-profile />
            </card-tabbed-tab>

            <card-tabbed-tab
                v-if="props.hasCompany"
                title="user:apiKeys.title"
                :localizetitle="true"
                value="api-keys"
            >
                <a v-if="props.tokens.length === 0" :href="route('user.apiKey')">
                    <button-primary>
                        <mf-translate domainkey="user:apiKeys.createApiKey"></mf-translate>
                    </button-primary>
                </a>
                <template v-else>
                    <ul>
                        <li
                            v-for="token in props.tokens"
                            :key="token.id"
                            class="inline-flex justify-between items-center px-2 -mx-2 h-8 group hover:bg-gray-50 space-x-2"
                        >
                            <code class="font-bold select-all">{{ token.token }}</code>
                            <span class="hidden group-hover:block">
                                <form
                                    :action="route('user.apiKey.delete', token.id)"
                                    method="POST"
                                >
                                    <input type="hidden" name="_token" :value="csrf">
                                    <input type="hidden" name="_method" value="DELETE">
                                    <button-danger
                                        size="sm"
                                        type="submit"
                                    >
                                        <i class="fa-regular fa-trash"></i>
                                    </button-danger>
                                </form>
                            </span>
                        </li>
                    </ul>
                    <div class="mt-4">
                        <create-personal-access-token-form />
                    </div>
                </template>
            </card-tabbed-tab>

            <card-tabbed-tab
                title="user:analyses.title"
                :localizetitle="true"
                value="analyses"
            >
                <ul class="divide-y">
                    <li
                        v-for="analysis in props.analyses"
                        :key="analysis.uuid"
                        class="flex justify-between py-1 group"
                    >
                        <span class="group-hover:text-gray-500">
                            <mf-translate domainkey="shared:date.createdAt"></mf-translate> {{ formattedDate(analysis.created_at) }}
                        </span>
                        <span>
                            <a
                                :href="route('expert.analyze.report', analysis.uuid)"
                                target="_blank"
                                class="hover:underline"
                            >
                                <mf-translate domainkey="shared:show"></mf-translate>
                            </a>
                        </span>
                    </li>
                </ul>
            </card-tabbed-tab>
        </card-tabbed>
    </div>
</template>
